import React, { useState } from 'react';
import { ArrowDown, CheckCircle2, Loader2 } from 'lucide-react';
import { capturePartnerLead } from '../../lib/api';

/**
 * OracleHero — the singular B2B entry to /partners.
 *
 * One field. One CTA. Three locked seals. Oracle-level minimal —
 * no scroll-reveal until the email is captured.
 *
 * On success, this component:
 *   - persists the unlock flag in localStorage so a refresh doesn't
 *     re-prompt the same hotelier,
 *   - invokes `onUnlocked()` so the page can begin its step-by-step
 *     reveal of the three sealed sections.
 */

const STORAGE_KEY = 'shreesavaa.partner.unlocked';

export const isPartnerUnlocked = () => {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(STORAGE_KEY) === '1'; }
  catch { return false; }
};

const setPartnerUnlocked = () => {
  try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
};

const STEPS = [
  { id: 'i',   label: 'Digital Product Passport' },
  { id: 'ii',  label: 'Atmosic IoT Witness' },
  { id: 'iii', label: 'ROI of Purity Audit' },
];

const OracleHero = ({ onUnlocked, alreadyUnlocked = false, testId = 'partner-oracle-hero' }) => {
  const [email, setEmail] = useState('');
  const [house, setHouse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Honour both the prop and a direct localStorage read so a hard refresh
  // doesn't briefly flash the form before the parent's effect runs.
  const [unlocked, setUnlocked] = useState(
    () => alreadyUnlocked || isPartnerUnlocked(),
  );
  const [err, setErr] = useState('');

  React.useEffect(() => {
    if (alreadyUnlocked && !unlocked) setUnlocked(true);
  }, [alreadyUnlocked, unlocked]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await capturePartnerLead({ email: email.trim(), house: house.trim() || undefined });
      setPartnerUnlocked();
      setUnlocked(true);
      if (typeof onUnlocked === 'function') onUnlocked();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(Array.isArray(detail) ? detail[0]?.msg || 'Could not capture your name.' : (detail || 'Could not capture your name.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid={testId}
      className="relative min-h-[88vh] flex flex-col justify-center px-6 md:px-12 py-24 md:py-32 bg-[#0E1A22] text-[#F4F8F9] overflow-hidden"
    >
      {/* Quiet ambient grain — physics, not decoration */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 22% 18%, #C1D7E2 0px, transparent 1px), radial-gradient(circle at 78% 42%, #AAC6D4 0px, transparent 1.4px), radial-gradient(circle at 53% 84%, #7C9CB1 0px, transparent 1.1px)',
          backgroundSize: '340px 340px',
        }}
      />
      {/* Gold horizon rule */}
      <span
        aria-hidden
        className="absolute top-24 left-12 right-12 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)' }}
      />

      <div className="relative max-w-[1400px] mx-auto w-full grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 items-center">
        {/* Headline column */}
        <div className="md:col-span-7">
          <p
            data-testid="partner-hero-overline"
            className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#D4AF5A] mb-7"
          >
            <span className="inline-block w-8 h-px bg-[#D4AF5A] mr-3 align-middle" />
            Partner Sutra · For the Independent Visionary
          </p>
          <h1
            data-testid="partner-hero-headline"
            className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[0.98]"
          >
            Atelier Nodes<span className="text-[#F4F8F9]/80">:</span>
            <br />
            <em className="text-[#F4F8F9]/85">For the Independent</em>
            <br />
            Visionary.
          </h1>
          <p
            data-testid="partner-hero-subline"
            className="mt-6 font-mono text-[11px] md:text-[12px] tracking-[0.32em] uppercase text-[#D4AF5A] font-semibold"
          >
            Built for owners who own their philosophy.
          </p>
          <p
            data-testid="partner-hero-thesis"
            className="mt-9 font-serif italic text-[#F4F8F9]/90 text-lg md:text-xl leading-relaxed max-w-2xl"
          >
            Shreesavaa is built for properties that own their philosophy.
            We install <span className="not-italic text-[#D4AF5A]">Sovereign Infrastructure</span>{' '}
            for those who choose <span className="not-italic text-[#D4AF5A]">Purity</span>{' '}
            over <span className="not-italic">Standardization</span>.
          </p>
          <p className="mt-5 font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/55 max-w-2xl leading-relaxed">
            Not a bulk-supply contract · Not a legacy franchise · A node in
            an atelier network — one owner-operator at a time.
          </p>
        </div>

        {/* Gate card */}
        <aside className="md:col-span-5">
          <div
            data-testid="partner-hero-gate"
            className="relative bg-[#F4F8F9]/[0.04] border border-[#F4F8F9]/25 backdrop-blur-sm p-8 md:p-10"
          >
            {!unlocked && (
              <form onSubmit={onSubmit} className="space-y-6">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70">
                  Request the Audit
                </p>

                <label className="block">
                  <span className="font-mono text-[9px] tracking-[0.28em] uppercase text-[#F4F8F9]/60">
                    Your house  <span className="text-[#F4F8F9]/40">· optional</span>
                  </span>
                  <input
                    type="text"
                    value={house}
                    onChange={(e) => setHouse(e.target.value)}
                    placeholder="Aman, Rosewood, your atelier hotel…"
                    data-testid="partner-hero-house-input"
                    className="mt-1 w-full bg-transparent border-b border-[#F4F8F9]/30 focus:border-[#F4F8F9] py-2 font-serif text-base text-[#F4F8F9] placeholder-[#F4F8F9]/40 focus:outline-none"
                  />
                </label>

                <label className="block">
                  <span className="font-mono text-[9px] tracking-[0.28em] uppercase text-[#F4F8F9]/60">
                    Your name in email
                  </span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@your-property.com"
                    data-testid="partner-hero-email-input"
                    className="mt-1 w-full bg-transparent border-b border-[#F4F8F9]/30 focus:border-[#F4F8F9] py-2 font-serif text-base text-[#F4F8F9] placeholder-[#F4F8F9]/40 focus:outline-none"
                  />
                </label>

                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  data-testid="partner-hero-cta"
                  className="group mt-2 w-full inline-flex items-center justify-center gap-3 px-7 py-4 text-[10px] tracking-[0.28em] uppercase font-medium text-[#0E1A22] bg-[#F4F8F9] hover:bg-[#C1D7E2] disabled:opacity-40 transition-colors"
                >
                  {submitting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : null}
                  {submitting ? 'Inscribing…' : 'Request the Partner Sutra & ROI Audit'}
                </button>

                {err && (
                  <p data-testid="partner-hero-error" className="font-mono text-[11px] text-[#E8A693]">
                    {err}
                  </p>
                )}

                {/* Sealed-steps preview — kept quiet, not loud */}
                <ul
                  data-testid="partner-hero-seals"
                  className="mt-8 pt-6 border-t border-[#F4F8F9]/15 space-y-3"
                >
                  {STEPS.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center justify-between font-mono text-[10px] tracking-[0.26em] uppercase text-[#F4F8F9]/60"
                    >
                      <span>{s.id}.&nbsp;&nbsp;{s.label}</span>
                      <span className="text-[#F4F8F9]/30">— sealed</span>
                    </li>
                  ))}
                </ul>
                <p className="font-mono text-[9px] tracking-[0.24em] uppercase text-[#F4F8F9]/45">
                  Each seal yields to the same name.
                </p>
              </form>
            )}

            {unlocked && (
              <div
                data-testid="partner-hero-unlocked"
                className="space-y-6 text-[#F4F8F9] story-fade-in"
              >
                <CheckCircle2 size={28} strokeWidth={1.2} className="text-[#F4F8F9]" />
                <h3 className="font-serif text-3xl font-light leading-tight">
                  The Sutra is opened to you.
                </h3>
                <p className="font-serif italic text-[#F4F8F9]/85 leading-relaxed">
                  Three thresholds wait below — read them in order. Atreya
                  proposes, Savitri arranges, Indra witnesses.
                </p>
                <ul className="space-y-3 font-mono text-[10px] tracking-[0.26em] uppercase text-[#F4F8F9]/85">
                  {STEPS.map((s) => (
                    <li key={s.id} className="flex items-center justify-between">
                      <span>{s.id}.&nbsp;&nbsp;{s.label}</span>
                      <span className="text-[#F4F8F9]/65">— unlocked</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid="partner-hero-descend"
                  onClick={() =>
                    document
                      .querySelector('[data-testid="partner-step-i"]')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  className="group mt-4 inline-flex items-center gap-3 px-6 py-3 border border-[#F4F8F9]/40 text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9] hover:bg-[#F4F8F9] hover:text-[#0E1A22] transition-colors"
                >
                  Descend to the first threshold
                  <ArrowDown
                    size={12}
                    strokeWidth={1.5}
                    className="transition-transform group-hover:translate-y-0.5"
                  />
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Scoped fade-in */}
      <style>{`
        .story-fade-in { animation: storyFadeIn 700ms ease-out both; }
        @keyframes storyFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
};

export default OracleHero;
